import type { AxiosRequestConfig } from 'axios';
import { parseServerUrls } from './serverUrls';

/**
 * API Configuration - Centralized settings for HTTP client
 */
export interface ApiConfig {
  /** Base URL for all API requests (the active/primary server). */
  baseURL: string;
  /**
   * Ordered list of server base URLs for automatic failover. The first entry is
   * the primary; the client rotates through the rest when a request can't reach
   * the current one. Populated from `VITE_API_URL` + `VITE_API_FALLBACK_URLS`.
   */
  baseURLs: string[];
  /** Request timeout in milliseconds */
  timeout: number;
  /** Default headers for all requests */
  headers: Record<string, string>;
  /** Retry configuration */
  retry: RetryConfig;
}

/**
 * Retry configuration
 */
export interface RetryConfig {
  /** Number of retry attempts */
  retries: number;
  /** Delay between retries in milliseconds */
  retryDelay: number;
  /** HTTP status codes to retry on */
  retryCondition: number[];
  /** Whether to use exponential backoff */
  exponentialBackoff: boolean;
}

/**
 * API Response wrapper
 */
export interface ApiResponse<T> {
  data: T;
  status: number;
  message?: string;
  success: boolean;
}

/**
 * API Error structure
 */
export interface ApiError {
  message: string;
  code?: string;
  status: number;
  errors?: Record<string, string[]>;
  timestamp?: string;
}

/**
 * Common backend error shapes.
 */
export type ApiErrorResponse =
  | string
  | {
      message?: string;
      title?: string;
      detail?: string;
      error?: string;
      code?: string;
      errors?: Record<string, string[]>;
    };

/**
 * Paginated request parameters
 */
export interface PaginationParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Paginated response
 */
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

/**
 * Request options extending Axios config
 */
export interface RequestOptions extends Omit<AxiosRequestConfig, 'url' | 'method'> {
  /** Skip authentication header */
  skipAuth?: boolean;
  /** Custom retry config for this request */
  retry?: Partial<RetryConfig>;
  /** Cache control */
  cache?: 'no-cache' | 'force-cache' | 'default';
}

/**
 * Default API configuration
 */
const primaryBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
const configuredServers = parseServerUrls(
  primaryBaseUrl,
  import.meta.env.VITE_API_FALLBACK_URLS as string | undefined
);

export const defaultApiConfig: ApiConfig = {
  baseURL: configuredServers[0] ?? primaryBaseUrl,
  baseURLs: configuredServers.length > 0 ? configuredServers : [primaryBaseUrl],
  timeout: Number(import.meta.env.VITE_API_TIMEOUT) || 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  retry: {
    retries: 3,
    retryDelay: 1000,
    retryCondition: [408, 429, 502, 503, 504],
    exponentialBackoff: true,
  },
};
